var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, Card, CardActions, CardContent, Checkbox, CircularProgress, Container, Divider, FormControl, FormControlLabel, InputLabel, LinearProgress, Link, MenuItem, Select, Stack, TextField, ThemeProvider, Typography, keyframes, } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningIcon from "@mui/icons-material/Warning";
import IosShare from "@mui/icons-material/IosShare";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import Heading from "./Heading";
import Footer from "./Footer";
import getTheme from "./Theme";
import React, { useEffect, useRef, useState } from "react";
import { ContentCardStacks } from "./components/ContentCardStacks";
import { ContentCard } from "./components/ContentCard";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { restoreSession, useProfileAuthStore, } from "./store/ProfileStore";
import { useNavigate, useSearchParams } from "react-router-dom";
import { saveLoginRedirect } from "./Auth/AuthUtils";
import { listChannelsUserCanPost } from "./store/ChannelStore";
import { useCacheBusterStore } from "./util/UrlUtils";
import { useLogger } from "./logger";
import { TopButtonMenu } from "./components/TopButtonMenu";
import { ChannelSelectionModal } from "./components/ChannelSelectionModal";
import { getAuthenticatedStudioURL } from "./ContentAPI";
import { useStudioJobStore, JOB_TYPES, getStudioJobDisplayTitle, getStudioJobThumbnailUrl, } from "./store/StudioJobStore";
import { getAvailableModelsForJobType, getStudioModel, } from "./interface";
import { JobCountdownTimer } from "./components/JobCountdownTimer";
import { useStudioImageUploader } from "./hooks/useStudioImageUploader";
const LOGPREFIX = "Studio: ";
// Helper function to format processing duration - shared across components
const formatProcessingDuration = (startedAt, completedAt) => {
    if (!startedAt || !completedAt)
        return null;
    const durationMs = completedAt - startedAt;
    const durationSeconds = Math.floor(durationMs / 1000);
    if (durationSeconds < 60) {
        return `${durationSeconds}s`;
    }
    else if (durationSeconds < 3600) {
        const minutes = Math.floor(durationSeconds / 60);
        const seconds = durationSeconds % 60;
        return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
    }
    else {
        const hours = Math.floor(durationSeconds / 3600);
        const minutes = Math.floor((durationSeconds % 3600) / 60);
        return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }
};
// CSS animations for job status indicators
const spinAnimation = keyframes `
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;
// Smart prompt text separation utility function
const parseJobPrompts = (job, modelId) => {
    // Function to get default prompts for the model
    const getDefaultPrompts = (selectedWorkflow) => {
        switch (selectedWorkflow) {
            case "Text-to-Image-1":
            case "Text-to-Image-2":
            case "Text-to-Image-3":
            case "Text-to-Image-4":
            case "Text-to-Image-5":
            case "Text-to-Image-6":
            case "Text-to-Image-7":
            case "Text-to-Image-8":
                return {
                    positive: "\n\nImage is highly detailed award winning cinematic photorealistic 8K.",
                    negative: "watermark, text, anime, cartoon, reflective surface, abnormal anatomy",
                };
            case "Text-to-Video-1":
                return {
                    positive: "\n\nVideo is highly detailed award winning cinematic photorealistic 8K.",
                    negative: "色调艳丽，过曝，静态，细节模糊不清，字幕，风格，作品，画作，画面，静止，整体发灰，最差质量，低质量，JPEG压缩残留，丑陋的，残缺的，多余的手指，画得不好的手部，画得不好的脸部，畸形的，毁容的，形态畸形的肢体，手指融合，静止不动的画面，杂乱的背景，三条腿，背景人很多，倒着走，裸露",
                };
            default:
                return {
                    positive: "\n\nImage is highly detailed award winning cinematic photorealistic 8K.",
                    negative: "watermark, text, anime, cartoon, reflective surface, abnormal anatomy",
                };
        }
    };
    const defaults = getDefaultPrompts(modelId);
    let userPositive = job.positivePrompt || "";
    let userNegative = job.negativePrompt || "";
    let usePositiveDefaults = false;
    let useNegativeDefaults = false;
    // Check if positive prompt ends with the expected default text
    if (userPositive && defaults.positive) {
        const defaultSuffix = "." + defaults.positive;
        if (userPositive.endsWith(defaultSuffix)) {
            // Remove the default text and set checkbox to checked
            userPositive = userPositive.substring(0, userPositive.length - defaultSuffix.length);
            usePositiveDefaults = true;
        }
        else if (userPositive.endsWith(defaults.positive.trim())) {
            // Handle case where it ends with default text without the added period
            userPositive = userPositive.substring(0, userPositive.length - defaults.positive.trim().length);
            usePositiveDefaults = true;
        }
    }
    // Check if negative prompt ends with the expected default text
    if (userNegative && defaults.negative) {
        const defaultSuffix = "." + defaults.negative;
        if (userNegative.endsWith(defaultSuffix)) {
            // Remove the default text and set checkbox to checked
            userNegative = userNegative.substring(0, userNegative.length - defaultSuffix.length);
            useNegativeDefaults = true;
        }
        else if (userNegative.endsWith(defaults.negative.trim())) {
            // Handle case where it ends with default text without the added period
            userNegative = userNegative.substring(0, userNegative.length - defaults.negative.trim().length);
            useNegativeDefaults = true;
        }
    }
    return {
        userPositive: userPositive.trim(),
        userNegative: userNegative.trim(),
        usePositiveDefaults,
        useNegativeDefaults,
    };
};
// Helper function to map workflow internal values to display names
const getWorkflowDisplayName = (workflow) => {
    switch (workflow) {
        case "Text-to-Image-1":
            return "Text-to-Image 1";
        case "Text-to-Image-2":
            return "Text-to-Image  2";
        case "Text-to-Image-3":
            return "Text-to-Image  3";
        case "Text-to-Image-4":
            return "Text-to-Image  4";
        case "Text-to-Image-5":
            return "Text-to-Image 5";
        case "Text-to-Image-6":
            return "Text-to-Image 6";
        case "Text-to-Image-7":
            return "Text-to-Image 7";
        case "Image-to-Image-1":
            return "Image-to-Image 1";
        //    case "Text-to-Video-1":
        //      return "Video 1";
        default:
            return workflow;
    }
};
const JobTypeCard = ({ jobType, onClick }) => {
    const isDisabled = !jobType.available;
    return (_jsx(ContentCard, { keyStr: `job-type-${jobType.id}`, title: jobType.title, backgroundColor: isDisabled ? "#F5F5F5" : getTheme().palette.backgrounds.main, textColor: isDisabled ? "#999999" : getTheme().palette.primary.contrastText, maxWidth: 0.22, mediaUrl: jobType.image, onMediaClick: isDisabled ? undefined : onClick, childComponent: _jsxs(Stack, Object.assign({ sx: { padding: "10px", textAlign: "center" } }, { children: [_jsx(Typography, Object.assign({ variant: "body2", sx: { marginBottom: "10px" } }, { children: jobType.description })), _jsx(Button, Object.assign({ variant: isDisabled ? "outlined" : "contained", disabled: isDisabled, onClick: isDisabled ? undefined : onClick, sx: { margin: "5px" }, startIcon: isDisabled ? undefined : _jsx(AddIcon, {}) }, { children: isDisabled ? "Coming Soon" : "Create New Job" }))] })) }, `job-type-${jobType.id}`));
};
const StudioJobCard = ({ job, onClick, onCopyToPost, onCopyToNewJob, modelDuration, onCountdownComplete, }) => {
    const getStatusColor = (status) => {
        switch (status) {
            case "pending":
                return "#FFA726"; // Orange
            case "processing":
                return "#42A5F5"; // Blue
            case "completed":
                return "#66BB6A"; // Green
            case "failed":
                return "#EF5350"; // Red
            default:
                return "#757575"; // Gray
        }
    };
    const getStatusText = (status, queuePosition) => {
        switch (status) {
            case "pending":
                console.log("Queue position for pending job:", queuePosition);
                return queuePosition ? `Queued (#${queuePosition})` : "Queued";
            case "processing":
                return "Processing...";
            case "completed":
                return "Completed";
            case "failed":
                return "Failed";
            default:
                return status;
        }
    };
    const thumbnailUrl = getStudioJobThumbnailUrl(job);
    const displayTitle = getStudioJobDisplayTitle(job);
    // Only show media for completed jobs with results
    const shouldShowMedia = job.jobStatus === "completed" && thumbnailUrl;
    // Determine if this job produces video content
    const isVideoJob = job.jobType === "text-to-video" || job.jobType === "image-to-video";
    return (_jsx(ContentCard, { keyStr: `studio-job-${job.jobId}`, title: displayTitle, backgroundColor: getTheme().palette.background.paper, textColor: getTheme().palette.text.primary, maxWidth: 0.22, mediaUrl: shouldShowMedia ? thumbnailUrl : undefined, mediaIsVideo: shouldShowMedia && isVideoJob ? true : undefined, onMediaClick: onClick, childComponent: _jsxs(Stack, Object.assign({ sx: {
                padding: "10px",
                cursor: "pointer",
                minHeight: "80px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
            }, onClick: onClick }, { children: [_jsxs(Stack, Object.assign({ sx: { flex: 1 } }, { children: [_jsxs(Stack, Object.assign({ direction: "row", alignItems: "center", justifyContent: "center", sx: {
                                marginBottom: "5px",
                                position: "relative",
                                minHeight: "24px",
                            } }, { children: [(job.jobStatus === "pending" ||
                                    job.jobStatus === "processing") && (_jsxs(Stack, Object.assign({ sx: {
                                        position: "absolute",
                                        left: "50%",
                                        transform: "translateX(-70px)",
                                    } }, { children: [job.jobStatus === "pending" && (_jsx(CircularProgress, { size: 16, sx: {
                                                color: getStatusColor(job.jobStatus),
                                            } })), job.jobStatus === "processing" && (_jsx(AutorenewIcon, { sx: {
                                                color: getStatusColor(job.jobStatus),
                                                fontSize: "1rem",
                                                animation: `${spinAnimation} 2s linear infinite`,
                                            } }))] }))), _jsx(Typography, Object.assign({ variant: "body2", sx: {
                                        color: getStatusColor(job.jobStatus),
                                        fontWeight: "bold",
                                        paddingLeft: job.jobStatus === "pending" ? "0" : "10px",
                                    } }, { children: getStatusText(job.jobStatus, job.queuePosition) }))] })), _jsx(Stack, Object.assign({ sx: { minHeight: "48px", marginBottom: "5px" } }, { children: _jsx(Typography, Object.assign({ variant: "caption", sx: {
                                    color: getTheme().palette.text.secondary,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: "vertical",
                                    minHeight: "45px", // Reserve space for 3 lines
                                } }, { children: job.positivePrompt })) })), job.errorMessage && (_jsx(Stack, Object.assign({ sx: { minHeight: "20px" } }, { children: _jsxs(Typography, Object.assign({ variant: "caption", sx: {
                                    color: getStatusColor("failed"),
                                    fontStyle: "italic",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 1,
                                    WebkitBoxOrient: "vertical",
                                } }, { children: ["Error:", " ", job.errorMessage.length > 50
                                        ? job.errorMessage.substring(0, 47) + "..."
                                        : job.errorMessage] })) }))), job.jobStatus === "completed" &&
                            formatProcessingDuration(job.startedAt, job.completedAt) && (_jsx(Stack, Object.assign({ sx: { minHeight: "20px" } }, { children: _jsxs(Typography, Object.assign({ variant: "caption", sx: {
                                    color: getTheme().palette.text.secondary,
                                    fontStyle: "italic",
                                    textAlign: "center",
                                } }, { children: ["Processing took:", " ", formatProcessingDuration(job.startedAt, job.completedAt)] })) }))), job.jobStatus === "processing" &&
                            modelDuration &&
                            onCountdownComplete && (_jsx(Stack, Object.assign({ sx: { minHeight: "20px", justifyContent: "center" } }, { children: _jsx(JobCountdownTimer, { job: job, modelDuration: modelDuration, onCountdownComplete: onCountdownComplete }) })))] })), job.jobStatus === "completed" && onCopyToPost && (_jsx(Stack, Object.assign({ sx: { minHeight: "36px", justifyContent: "flex-end" } }, { children: _jsx(Button, Object.assign({ variant: "contained", size: "small", sx: {
                            fontSize: "0.75rem",
                            padding: "4px 8px",
                        }, onClick: (e) => {
                            e.stopPropagation();
                            onCopyToPost(job);
                        }, startIcon: _jsx(IosShare, {}) }, { children: "Create Post" })) }))), job.jobStatus === "failed" && onCopyToNewJob && (_jsx(Stack, Object.assign({ sx: { minHeight: "36px", justifyContent: "flex-end" } }, { children: _jsx(Button, Object.assign({ variant: "outlined", size: "small", sx: {
                            fontSize: "0.75rem",
                            padding: "4px 8px",
                        }, onClick: (e) => {
                            e.stopPropagation();
                            onCopyToNewJob(job);
                        }, startIcon: _jsx(ContentCopyIcon, {}) }, { children: "Copy to New Job" })) })))] })) }, `studio-job-${job.jobId}`));
};
const NewStudioJobForm = ({ jobType, onSubmit, onCancel, loading, auth, logger, initialJobData, recentMediaPath, }) => {
    var _a;
    // Function to get default prompts for each workflow using model configuration
    const getDefaultPrompts = (selectedWorkflow) => {
        try {
            const model = getStudioModel(selectedWorkflow);
            return {
                positive: "\n\n" + model.defaultPositivePrompt,
                negative: model.defaultNegativePrompt,
            };
        }
        catch (_a) {
            // Fallback to generic defaults if model not found
            return {
                positive: "\n\nImage is highly detailed award winning cinematic photorealistic 8K.",
                negative: "watermark, text, anime, cartoon, reflective surface, abnormal anatomy",
            };
        }
    };
    // Image upload hook for image-to-image jobs
    const imageUploader = useStudioImageUploader();
    // State variables
    const [title, setTitle] = useState("");
    // Initialize workflow based on job type
    const initialWorkflow = jobType === "text-to-video"
        ? "Text-to-Video-1"
        : jobType === "image-to-image"
            ? "Image-to-Image-1"
            : "Text-to-Image-1";
    const [workflow, setWorkflow] = useState(initialWorkflow);
    // Image upload state
    const [sourceImagePath, setSourceImagePath] = useState("");
    const [imageUploadError, setImageUploadError] = useState("");
    const [imageSourceSelection, setImageSourceSelection] = useState("source");
    const [outputImagePath, setOutputImagePath] = useState(null);
    const [sourceImageAvailable, setSourceImageAvailable] = useState(false);
    const [outputImageAvailable, setOutputImageAvailable] = useState(false);
    // Video-specific parameters
    const [videoWidth, setVideoWidth] = useState(480);
    const [videoHeight, setVideoHeight] = useState(480);
    const [videoLength, setVideoLength] = useState(81);
    const [videoFPS, setVideoFPS] = useState(16);
    const [videoSeed, setVideoSeed] = useState(undefined);
    // Initialize prompts as empty strings (changed behavior)
    const [positivePrompt, setPositivePrompt] = useState("");
    const [negativePrompt, setNegativePrompt] = useState("");
    // Checkbox states for including default prompts (checked by default)
    const [usePositiveDefaults, setUsePositiveDefaults] = useState(true);
    const [useNegativeDefaults, setUseNegativeDefaults] = useState(true);
    // State for expanding/collapsing default prompt text in labels
    const [showFullPositiveText, setShowFullPositiveText] = useState(false);
    const [showFullNegativeText, setShowFullNegativeText] = useState(false);
    // Initialize form with job data when copying
    useEffect(() => {
        if (initialJobData) {
            // Parse the job prompts to extract user data and checkbox states
            const parsedPrompts = parseJobPrompts(initialJobData, initialJobData.modelId);
            // Set form values from the copied job
            setTitle(initialJobData.title || "");
            setWorkflow(initialJobData.modelId);
            setPositivePrompt(parsedPrompts.userPositive);
            setNegativePrompt(parsedPrompts.userNegative);
            setUsePositiveDefaults(parsedPrompts.usePositiveDefaults);
            setUseNegativeDefaults(parsedPrompts.useNegativeDefaults);
            // Set up image source selection for image-to-image jobs
            if (initialJobData.jobType === "image-to-image" ||
                initialJobData.jobType === "image-to-video") {
                // Check if source image is available
                const sourceAvailable = !!(recentMediaPath || initialJobData.sourceImagePath);
                setSourceImageAvailable(sourceAvailable);
                // Check if output image is available
                const outputAvailable = !!(initialJobData.jobStatus === "completed" &&
                    initialJobData.jobFiles &&
                    initialJobData.jobFiles.length > 0);
                setOutputImageAvailable(outputAvailable);
                // Set output image path if available
                if (outputAvailable) {
                    setOutputImagePath(initialJobData.jobFiles[0]);
                }
                // Initialize source image path and selection
                if (sourceAvailable) {
                    if (recentMediaPath) {
                        setSourceImagePath(recentMediaPath);
                        logger.log("Source image path copied from recent media: " + recentMediaPath);
                    }
                    else if (initialJobData.sourceImagePath) {
                        setSourceImagePath(initialJobData.sourceImagePath);
                        logger.log("Source image path copied from job: " +
                            initialJobData.sourceImagePath);
                    }
                    setImageSourceSelection("source"); // Default to source
                }
                else if (outputAvailable) {
                    // If no source but output available, default to output
                    setImageSourceSelection("output");
                    setSourceImagePath(initialJobData.jobFiles[0]);
                }
            }
            // Copy video parameters if applicable
            if (initialJobData.jobType === "text-to-video") {
                if (initialJobData.videoWidth)
                    setVideoWidth(initialJobData.videoWidth);
                if (initialJobData.videoHeight)
                    setVideoHeight(initialJobData.videoHeight);
                if (initialJobData.videoLength)
                    setVideoLength(initialJobData.videoLength);
                if (initialJobData.videoFPS)
                    setVideoFPS(initialJobData.videoFPS);
                if (initialJobData.videoSeed)
                    setVideoSeed(initialJobData.videoSeed);
            }
        }
    }, [initialJobData, recentMediaPath]);
    // Helper function to create checkbox label with expandable text
    const createExpandableLabel = (promptType, defaultText, isExpanded, onToggleExpanded) => {
        const maxLength = 15; // Very short truncation for clean UI
        const cleanText = defaultText.trim();
        const needsTruncation = cleanText.length > maxLength;
        const displayText = needsTruncation && !isExpanded
            ? cleanText.substring(0, maxLength).trim()
            : cleanText;
        return (_jsxs(Typography, Object.assign({ variant: "body2", component: "span", sx: {
                display: "inline",
                lineHeight: 1,
                verticalAlign: "left",
            } }, { children: ["Include recommended text -", _jsx(Typography, Object.assign({ component: "em", variant: "body2", sx: {
                        color: "text.secondary",
                        fontStyle: "italic",
                        verticalAlign: "left",
                    } }, { children: displayText })), needsTruncation && (_jsx(Typography, Object.assign({ component: "span", variant: "body2", sx: {
                        color: "primary.main",
                        cursor: "pointer",
                        textDecoration: "underline",
                        marginLeft: "4px",
                        verticalAlign: "middle",
                        "&:hover": {
                            color: "primary.dark",
                        },
                    }, onClick: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onToggleExpanded();
                    } }, { children: isExpanded ? " (less)" : "... (more)" })))] })));
    };
    // Update prompts when workflow changes (no longer auto-populates)
    const handleWorkflowChange = (newWorkflow) => {
        setWorkflow(newWorkflow);
        // Reset expansion states when workflow changes
        setShowFullPositiveText(false);
        setShowFullNegativeText(false);
        // Note: We no longer auto-populate prompts when workflow changes
        // User prompts remain as they are, defaults are handled via checkboxes
    };
    // Execute job creation with reCAPTCHA token
    const doSubmitWithToken = (token) => __awaiter(void 0, void 0, void 0, function* () {
        const defaults = getDefaultPrompts(workflow);
        // Build final positive prompt
        let finalPositivePrompt = positivePrompt.trim();
        if (usePositiveDefaults) {
            if (finalPositivePrompt) {
                finalPositivePrompt += "." + defaults.positive;
            }
            else {
                finalPositivePrompt = defaults.positive.trim();
            }
        }
        // Build final negative prompt
        let finalNegativePrompt = negativePrompt.trim();
        if (useNegativeDefaults) {
            if (finalNegativePrompt) {
                finalNegativePrompt += "." + defaults.negative;
            }
            else {
                finalNegativePrompt = defaults.negative.trim();
            }
        }
        const input = {
            jobType,
            jobOwnerId: auth.userId ? auth.userId : "",
            modelId: workflow,
            positivePrompt: finalPositivePrompt,
            negativePrompt: finalNegativePrompt || undefined,
            title: title.trim(),
            // Video parameters (only relevant for video jobs)
            videoWidth: jobType === "text-to-video" ? videoWidth : undefined,
            videoHeight: jobType === "text-to-video" ? videoHeight : undefined,
            videoLength: jobType === "text-to-video" ? videoLength : undefined,
            videoFPS: jobType === "text-to-video" ? videoFPS : undefined,
            videoSeed: jobType === "text-to-video" ? videoSeed : undefined,
            // Image-to-image parameters
            sourceImagePath: jobType === "image-to-image" ? sourceImagePath : undefined,
            recaptchaToken: token,
        };
        yield onSubmit(input);
    });
    const handleSubmit = () => __awaiter(void 0, void 0, void 0, function* () {
        var _b, _c;
        // Basic validation
        if (!positivePrompt.trim() || !title.trim()) {
            return;
        }
        // For image-to-image jobs, require source image
        if (jobType === "image-to-image" && !sourceImagePath) {
            setImageUploadError("Please upload a source image for image-to-image jobs.");
            return;
        }
        const grecaptcha = window.grecaptcha;
        if (grecaptcha &&
            ((_b = grecaptcha.enterprise) === null || _b === void 0 ? void 0 : _b.ready) &&
            ((_c = grecaptcha.enterprise) === null || _c === void 0 ? void 0 : _c.execute)) {
            grecaptcha.enterprise.ready(() => __awaiter(void 0, void 0, void 0, function* () {
                grecaptcha.enterprise
                    .execute("6LeMTvYqAAAAAEXBRStAkzbH267Azriet8mmhqMq", {
                    action: "submit",
                })
                    .then((token) => {
                    doSubmitWithToken(token);
                });
            }));
        }
        else {
            doSubmitWithToken("grecaptcha-not-loaded");
        }
    });
    return (_jsx(ContentCard, { keyStr: "new-studio-job-form", title: `New ${(_a = JOB_TYPES.find((t) => t.id === jobType)) === null || _a === void 0 ? void 0 : _a.title} Job`, backgroundColor: getTheme().palette.background.paper, textColor: getTheme().palette.text.primary, maxWidth: 0.6, childComponent: _jsxs(Stack, Object.assign({ sx: { padding: "20px" }, spacing: 1.5 }, { children: [_jsx(Typography, Object.assign({ variant: "h6", color: "primary" }, { children: "Model" })), _jsxs(FormControl, Object.assign({ fullWidth: true, size: "small" }, { children: [_jsx(InputLabel, { children: "Model" }), _jsx(Select, Object.assign({ value: workflow, onChange: (e) => handleWorkflowChange(e.target.value), disabled: loading, label: "Model" }, { children: getAvailableModelsForJobType(jobType).map((model) => (_jsx(MenuItem, Object.assign({ value: model.id }, { children: model.displayName }), model.id))) }))] })), jobType === "image-to-image" && (_jsxs(_Fragment, { children: [_jsx(Typography, Object.assign({ variant: "h6", color: "primary" }, { children: "Source Image" })), initialJobData &&
                            (sourceImageAvailable || outputImageAvailable) && (_jsxs(Stack, Object.assign({ spacing: 2 }, { children: [_jsx(Typography, Object.assign({ variant: "body2", color: "text.secondary" }, { children: "Choose an image source for this job:" })), _jsxs(Stack, Object.assign({ spacing: 2 }, { children: [sourceImageAvailable && (_jsx(Card, Object.assign({ sx: {
                                                border: imageSourceSelection === "source"
                                                    ? "2px solid #1976d2"
                                                    : "1px solid #e0e0e0",
                                                cursor: "pointer",
                                                "&:hover": { backgroundColor: "#f5f5f5" },
                                            }, onClick: () => {
                                                setImageSourceSelection("source");
                                                if (recentMediaPath) {
                                                    setSourceImagePath(recentMediaPath);
                                                }
                                                else if (initialJobData.sourceImagePath) {
                                                    setSourceImagePath(initialJobData.sourceImagePath);
                                                }
                                                // Clear upload state when switching away from upload
                                                imageUploader.clearPreview();
                                            } }, { children: _jsx(CardContent, Object.assign({ sx: { padding: "12px !important" } }, { children: _jsxs(Stack, Object.assign({ direction: "row", spacing: 2, alignItems: "center" }, { children: [_jsx("input", { type: "radio", checked: imageSourceSelection === "source", onChange: () => {
                                                                setImageSourceSelection("source");
                                                                if (recentMediaPath) {
                                                                    setSourceImagePath(recentMediaPath);
                                                                }
                                                                else if (initialJobData.sourceImagePath) {
                                                                    setSourceImagePath(initialJobData.sourceImagePath);
                                                                }
                                                            } }), _jsxs(Stack, Object.assign({ sx: { flex: 1 } }, { children: [_jsx(Typography, Object.assign({ variant: "body2", fontWeight: "bold" }, { children: "Use Original Source Image" })), _jsx(Typography, Object.assign({ variant: "caption", color: "text.secondary" }, { children: "The input image from the original job" }))] })), (recentMediaPath ||
                                                            initialJobData.sourceImagePath) && (_jsx(Card, Object.assign({ sx: { width: 80, height: 60 } }, { children: _jsx(CardContent, Object.assign({ sx: { padding: "4px !important" } }, { children: _jsx("img", { src: getAuthenticatedStudioURL(recentMediaPath ||
                                                                        initialJobData.sourceImagePath ||
                                                                        "", auth.bearer), alt: "Source preview", style: {
                                                                        width: "100%",
                                                                        height: "52px",
                                                                        objectFit: "cover",
                                                                        borderRadius: "4px",
                                                                    } }) })) })))] })) })) }))), outputImageAvailable && outputImagePath && (_jsx(Card, Object.assign({ sx: {
                                                border: imageSourceSelection === "output"
                                                    ? "2px solid #1976d2"
                                                    : "1px solid #e0e0e0",
                                                cursor: "pointer",
                                                "&:hover": { backgroundColor: "#f5f5f5" },
                                            }, onClick: () => {
                                                setImageSourceSelection("output");
                                                setSourceImagePath(outputImagePath);
                                                // Clear upload state when switching away from upload
                                                imageUploader.clearPreview();
                                            } }, { children: _jsx(CardContent, Object.assign({ sx: { padding: "12px !important" } }, { children: _jsxs(Stack, Object.assign({ direction: "row", spacing: 2, alignItems: "center" }, { children: [_jsx("input", { type: "radio", checked: imageSourceSelection === "output", onChange: () => {
                                                                setImageSourceSelection("output");
                                                                setSourceImagePath(outputImagePath);
                                                            } }), _jsxs(Stack, Object.assign({ sx: { flex: 1 } }, { children: [_jsx(Typography, Object.assign({ variant: "body2", fontWeight: "bold" }, { children: "Use Generated Output Image" })), _jsx(Typography, Object.assign({ variant: "caption", color: "text.secondary" }, { children: "The result from the completed job" }))] })), _jsx(Card, Object.assign({ sx: { width: 80, height: 60 } }, { children: _jsx(CardContent, Object.assign({ sx: { padding: "4px !important" } }, { children: _jsx("img", { src: getAuthenticatedStudioURL(outputImagePath, auth.bearer), alt: "loading...", "aria-label": "Output Preview", style: {
                                                                        width: "100%",
                                                                        height: "52px",
                                                                        objectFit: "cover",
                                                                        borderRadius: "4px",
                                                                    } }) })) }))] })) })) }))), _jsx(Card, Object.assign({ sx: {
                                                border: imageSourceSelection === "upload"
                                                    ? "2px solid #1976d2"
                                                    : "1px solid #e0e0e0",
                                                cursor: "pointer",
                                                "&:hover": { backgroundColor: "#f5f5f5" },
                                            }, onClick: () => setImageSourceSelection("upload") }, { children: _jsx(CardContent, Object.assign({ sx: { padding: "12px !important" } }, { children: _jsxs(Stack, Object.assign({ direction: "row", spacing: 2, alignItems: "center" }, { children: [_jsx("input", { type: "radio", checked: imageSourceSelection === "upload", onChange: () => setImageSourceSelection("upload") }), _jsxs(Stack, Object.assign({ sx: { flex: 1 } }, { children: [_jsx(Typography, Object.assign({ variant: "body2", fontWeight: "bold" }, { children: "Upload New Image" })), _jsx(Typography, Object.assign({ variant: "caption", color: "text.secondary" }, { children: "Choose a different image file" }))] })), _jsx(CloudUploadIcon, { color: "action" })] })) })) }))] }))] }))), (!initialJobData || imageSourceSelection === "upload") && (_jsxs(Stack, Object.assign({ spacing: 2 }, { children: [_jsxs(Button, Object.assign({ variant: "outlined", component: "label", startIcon: _jsx(CloudUploadIcon, {}), disabled: loading || imageUploader.isUploading, sx: { alignSelf: "flex-start" } }, { children: [imageUploader.isUploading
                                            ? "Uploading..."
                                            : "Upload Image", _jsx("input", { type: "file", accept: "image/jpeg,image/png", hidden: true, onChange: (e) => __awaiter(void 0, void 0, void 0, function* () {
                                                var _d;
                                                const file = (_d = e.target.files) === null || _d === void 0 ? void 0 : _d[0];
                                                if (file) {
                                                    setImageUploadError("");
                                                    setImageSourceSelection("upload"); // Switch to upload mode
                                                    try {
                                                        const result = yield imageUploader.uploadImage({
                                                            file,
                                                            auth,
                                                        });
                                                        if (result.success && result.imageKey) {
                                                            setSourceImagePath(result.imageKey);
                                                        }
                                                        else {
                                                            setImageUploadError(result.error || "Upload failed");
                                                        }
                                                    }
                                                    catch (error) {
                                                        setImageUploadError(error.message);
                                                    }
                                                }
                                            }) })] })), imageUploader.isUploading && (_jsxs(Stack, Object.assign({ spacing: 1 }, { children: [_jsx(LinearProgress, { variant: "determinate", value: imageUploader.uploadProgress }), _jsxs(Typography, Object.assign({ variant: "caption", color: "text.secondary" }, { children: ["Uploading image... ", imageUploader.uploadProgress, "%"] }))] }))), imageUploader.error && (_jsxs(Typography, Object.assign({ variant: "body2", color: "error" }, { children: ["Upload Error: ", imageUploader.error.message] }))), imageUploadError && (_jsx(Typography, Object.assign({ variant: "body2", color: "error" }, { children: imageUploadError }))), _jsx(Typography, Object.assign({ variant: "caption", color: "text.secondary" }, { children: "Supported formats: JPEG, PNG (max 50MB)" }))] }))), ((imageSourceSelection === "upload" &&
                            imageUploader.previewUrl) ||
                            (imageSourceSelection !== "upload" && sourceImagePath)) && (_jsxs(Stack, Object.assign({ spacing: 1 }, { children: [_jsxs(Typography, Object.assign({ variant: "body2", color: "success.main" }, { children: ["\u2713", " ", imageSourceSelection === "upload"
                                            ? "Image uploaded successfully"
                                            : imageSourceSelection === "source"
                                                ? "Using original source image"
                                                : imageSourceSelection === "output"
                                                    ? "Using generated output image"
                                                    : "Using selected image"] })), _jsx(Card, Object.assign({ sx: { maxWidth: 300, alignSelf: "flex-start" } }, { children: _jsx(CardContent, Object.assign({ sx: { padding: "8px !important" } }, { children: _jsx("img", { src: imageSourceSelection === "upload" &&
                                                imageUploader.previewUrl
                                                ? imageUploader.previewUrl // Use blob URL directly for uploads
                                                : getAuthenticatedStudioURL(sourceImagePath, auth.bearer), alt: "loading...", "aria-label": "Source Preview", style: {
                                                width: "100%",
                                                height: "auto",
                                                maxHeight: "200px",
                                                objectFit: "contain",
                                                borderRadius: "4px",
                                            } }) })) }))] })))] })), _jsx(Typography, Object.assign({ variant: "h6", color: "primary" }, { children: "Title" })), _jsx(TextField, { fullWidth: true, required: true, label: "Give your job a name.", value: title, onChange: (e) => setTitle(e.target.value), disabled: loading, size: "small" }), _jsx(Typography, Object.assign({ variant: "h6", color: "primary" }, { children: "Positive Prompt" })), _jsx(TextField, { fullWidth: true, required: true, label: "Describe what you want to see.", multiline: true, rows: 3, value: positivePrompt, onChange: (e) => setPositivePrompt(e.target.value), disabled: loading, "data-testid": "positive-prompt-field", sx: { paddingBottom: 0, marginBottom: 0 } }), _jsx(FormControlLabel, { control: _jsx(Checkbox, { checked: usePositiveDefaults, onChange: (e) => setUsePositiveDefaults(e.target.checked), disabled: loading, "data-testid": "positive-defaults-checkbox" }), label: createExpandableLabel("positive", getDefaultPrompts(workflow).positive.trim(), showFullPositiveText, () => setShowFullPositiveText(!showFullPositiveText)), sx: {
                        paddingTop: 0,
                        textAlign: "left",
                        alignItems: "left",
                        marginBottom: "8px",
                    } }), _jsx(Typography, Object.assign({ variant: "h6", color: "primary" }, { children: "Negative Prompt" })), _jsx(TextField, { fullWidth: true, label: "Describe what you do NOT want to see.", multiline: true, rows: 2, value: negativePrompt, onChange: (e) => setNegativePrompt(e.target.value), disabled: loading, "data-testid": "negative-prompt-field" }), _jsx(FormControlLabel, { control: _jsx(Checkbox, { checked: useNegativeDefaults, onChange: (e) => setUseNegativeDefaults(e.target.checked), disabled: loading, "data-testid": "negative-defaults-checkbox" }), label: createExpandableLabel("negative", getDefaultPrompts(workflow).negative.trim(), showFullNegativeText, () => setShowFullNegativeText(!showFullNegativeText)), sx: {
                        textAlign: "left",
                        alignItems: "left",
                        marginBottom: "8px,",
                    } }), jobType === "text-to-video" && (_jsx(_Fragment, { children: _jsx(Stack, Object.assign({ direction: "row", spacing: 2 }, { children: _jsx(TextField, { fullWidth: true, label: "Seed (optional)", type: "number", value: videoSeed || "", onChange: (e) => setVideoSeed(e.target.value ? parseInt(e.target.value) : undefined), disabled: loading, size: "small", helperText: "Random seed for reproducibility" }) })) })), _jsxs(Stack, Object.assign({ direction: "row", spacing: 2, sx: { justifyContent: "center", marginTop: "20px" } }, { children: [_jsx(Button, Object.assign({ variant: "contained", onClick: handleSubmit, disabled: loading ||
                                !positivePrompt.trim() ||
                                !title.trim() ||
                                (jobType === "image-to-image" &&
                                    (!sourceImagePath ||
                                        (imageSourceSelection === "upload" &&
                                            !imageUploader.previewUrl))), startIcon: loading ? _jsx(CircularProgress, { size: 20 }) : _jsx(SmartToyIcon, {}) }, { children: loading ? "Creating..." : "Create Job" })), _jsx(Button, Object.assign({ variant: "outlined", onClick: onCancel, disabled: loading }, { children: "Cancel" }))] })), _jsxs(Typography, Object.assign({ variant: "caption", color: "text.secondary", sx: { textAlign: "center", marginTop: 2 } }, { children: ["This site is protected by reCAPTCHA and the Google", " ", _jsx(Link, Object.assign({ href: "https://policies.google.com/privacy", target: "_blank", rel: "noopener" }, { children: "Privacy Policy" })), " ", "and", " ", _jsx(Link, Object.assign({ href: "https://policies.google.com/terms", target: "_blank", rel: "noopener" }, { children: "Terms of Service" })), " ", "apply."] }))] })) }, "new-studio-job-form"));
};
const JobDetail = ({ job, onBack, onCopyToPost, onCopyToNewJob, }) => {
    const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
    const [showDeleteSuccessDialog, setShowDeleteSuccessDialog] = useState(false);
    const [deleteInProgress, setDeleteInProgress] = useState(false);
    const [deleteError, setDeleteError] = useState(undefined);
    const auth = useProfileAuthStore((state) => state);
    const cacheBusterStore = useCacheBusterStore((state) => state);
    const logger = useLogger((state) => state);
    const studioJobStore = useStudioJobStore();
    const handleDeleteClick = () => {
        setDeleteError(undefined);
        setShowDeleteConfirmDialog(true);
    };
    const handleDeleteConfirm = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setDeleteInProgress(true);
            setDeleteError(undefined);
            yield studioJobStore.deleteJob(job.jobId, auth, cacheBusterStore, logger);
            setShowDeleteConfirmDialog(false);
            setShowDeleteSuccessDialog(true);
        }
        catch (error) {
            logger.error("Error deleting job: " + error.message);
            setDeleteError(error.message);
        }
        finally {
            setDeleteInProgress(false);
        }
    });
    const handleDeleteCancel = () => {
        setShowDeleteConfirmDialog(false);
        setDeleteError(undefined);
    };
    const handleDeleteSuccessOk = () => {
        setShowDeleteSuccessDialog(false);
        onBack();
    };
    const getStatusColor = (status) => {
        switch (status) {
            case "pending":
                return "#FFA726";
            case "processing":
                return "#42A5F5";
            case "completed":
                return "#66BB6A";
            case "failed":
                return "#EF5350";
            default:
                return "#757575";
        }
    };
    const getStatusText = (status, queuePosition) => {
        switch (status) {
            case "pending":
                return queuePosition ? `Queued (#${queuePosition})` : "Queued";
            case "processing":
                return "Processing...";
            case "completed":
                return "Completed";
            case "failed":
                return "Failed";
            default:
                return status;
        }
    };
    const thumbnailUrl = getStudioJobThumbnailUrl(job);
    const displayTitle = getStudioJobDisplayTitle(job);
    // Determine if this job produces video content
    const isVideoJob = job.jobType === "text-to-video" || job.jobType === "image-to-video";
    const shouldShowMedia = job.jobStatus === "completed" && thumbnailUrl;
    return (_jsxs(Stack, Object.assign({ sx: { padding: "20px", alignItems: "center" }, spacing: 2 }, { children: [showDeleteConfirmDialog && (_jsx(Stack, Object.assign({ sx: { width: "100%", maxWidth: "600px" } }, { children: _jsxs(Card, Object.assign({ sx: { backgroundColor: "#FFF3E0", border: "1px solid #FF9800" } }, { children: [_jsx(CardContent, { children: _jsxs(Stack, Object.assign({ direction: "row", spacing: 2, alignItems: "flex-start" }, { children: [_jsx(WarningIcon, { sx: { color: "#FF9800", marginTop: "2px" } }), _jsxs(Stack, Object.assign({ spacing: 2, sx: { flex: 1 } }, { children: [_jsx(Typography, Object.assign({ variant: "h6", sx: { color: "#E65100", fontWeight: "bold" } }, { children: "Confirm Delete" })), _jsx(Typography, Object.assign({ variant: "body2", sx: { color: "#E65100" } }, { children: "Are you sure you want to delete this job?" })), _jsx(Typography, Object.assign({ variant: "body2", sx: { color: "#E65100", fontStyle: "italic" } }, { children: "This action cannot be undone." })), deleteInProgress && (_jsxs(Stack, Object.assign({ sx: { marginTop: 2 } }, { children: [_jsx(LinearProgress, {}), _jsx(Typography, Object.assign({ variant: "body2", sx: { marginTop: 1, color: "#E65100" } }, { children: "Deleting job..." }))] }))), deleteError && (_jsxs(Typography, Object.assign({ variant: "body2", sx: {
                                                    color: "#EF5350",
                                                    padding: 1,
                                                    backgroundColor: "#FFEBEE",
                                                    borderRadius: "4px",
                                                } }, { children: ["Error: ", deleteError] })))] }))] })) }), _jsx(CardActions, { children: _jsxs(Stack, Object.assign({ direction: "row", sx: {
                                    display: "flex",
                                    width: "100%",
                                    justifyContent: "center",
                                } }, { children: [_jsx(Button, Object.assign({ onClick: handleDeleteCancel, disabled: deleteInProgress, sx: { margin: "0.5em" }, variant: "outlined" }, { children: "No" })), _jsx(Button, Object.assign({ onClick: handleDeleteConfirm, disabled: deleteInProgress, sx: { margin: "0.5em" }, variant: "contained", color: "error" }, { children: "Yes, Delete" }))] })) })] })) }))), showDeleteSuccessDialog && (_jsx(Stack, Object.assign({ sx: { width: "100%", maxWidth: "600px" } }, { children: _jsxs(Card, Object.assign({ sx: { backgroundColor: "#E8F5E8", border: "1px solid #4CAF50" } }, { children: [_jsx(CardContent, { children: _jsxs(Stack, Object.assign({ direction: "row", spacing: 2, alignItems: "flex-start" }, { children: [_jsx(CheckCircleIcon, { sx: { color: "#4CAF50", marginTop: "2px" } }), _jsxs(Stack, Object.assign({ spacing: 2, sx: { flex: 1 } }, { children: [_jsx(Typography, Object.assign({ variant: "h6", sx: { color: "#2E7D32", fontWeight: "bold" } }, { children: "Job Deleted Successfully!" })), _jsx(Typography, Object.assign({ variant: "body2", sx: { color: "#2E7D32" } }, { children: "The job has been permanently deleted." }))] }))] })) }), _jsx(CardActions, { children: _jsx(Stack, Object.assign({ direction: "row", sx: {
                                    display: "flex",
                                    width: "100%",
                                    justifyContent: "center",
                                } }, { children: _jsx(Button, Object.assign({ onClick: handleDeleteSuccessOk, sx: { margin: "0.5em" }, variant: "contained", color: "primary" }, { children: "OK" })) })) })] })) }))), !showDeleteConfirmDialog && !showDeleteSuccessDialog && (_jsxs(_Fragment, { children: [_jsx(Stack, Object.assign({ direction: "row", sx: {
                            width: "100%",
                            justifyContent: "flex-end",
                            maxWidth: "100%",
                            padding: "0",
                        } }, { children: _jsx(Button, Object.assign({ variant: "contained", onClick: onBack, startIcon: _jsx(CloseIcon, {}), sx: { margin: "0.5em" } }, { children: "Close" })) })), _jsx(ContentCard, { keyStr: "job-detail", title: displayTitle, backgroundColor: getTheme().palette.background.paper, textColor: getTheme().palette.text.primary, maxWidth: 0.8, mediaUrl: shouldShowMedia ? thumbnailUrl : undefined, mediaIsVideo: shouldShowMedia && isVideoJob ? true : undefined, childComponent: _jsxs(Stack, Object.assign({ sx: { padding: "20px" }, spacing: 1 }, { children: [_jsx(Typography, Object.assign({ variant: "h6", sx: {
                                        color: getStatusColor(job.jobStatus),
                                        fontWeight: "bold",
                                        textAlign: "center",
                                    } }, { children: getStatusText(job.jobStatus, job.queuePosition) })), _jsx(Typography, Object.assign({ variant: "body1", sx: { fontWeight: "bold" } }, { children: "Positive Prompt:" })), _jsx(Typography, Object.assign({ variant: "body2" }, { children: job.positivePrompt })), job.negativePrompt && (_jsxs(_Fragment, { children: [_jsx(Typography, Object.assign({ variant: "body1", sx: { fontWeight: "bold" } }, { children: "Negative Prompt:" })), _jsx(Typography, Object.assign({ variant: "body2" }, { children: job.negativePrompt }))] })), _jsx(Typography, Object.assign({ variant: "body1", sx: { fontWeight: "bold" } }, { children: "Model:" })), _jsx(Typography, Object.assign({ variant: "body2" }, { children: getWorkflowDisplayName(job.modelId) })), _jsx(Typography, Object.assign({ variant: "body1", sx: { fontWeight: "bold" } }, { children: "Job ID:" })), _jsx(Typography, Object.assign({ variant: "body2", sx: {
                                        fontFamily: "monospace",
                                        backgroundColor: "#F5F5F5",
                                        padding: "4px 8px",
                                        borderRadius: "4px",
                                        fontSize: "0.85rem",
                                    } }, { children: job.jobId })), job.jobStatus === "completed" &&
                                    formatProcessingDuration(job.startedAt, job.completedAt) && (_jsxs(_Fragment, { children: [_jsx(Typography, Object.assign({ variant: "body1", sx: { fontWeight: "bold" } }, { children: "Processing Duration:" })), _jsx(Typography, Object.assign({ variant: "body2" }, { children: formatProcessingDuration(job.startedAt, job.completedAt) }))] })), job.jobStatus === "pending" && (_jsxs(_Fragment, { children: [_jsx(Typography, Object.assign({ variant: "body1", sx: { fontWeight: "bold" } }, { children: "Submitted:" })), _jsx(Typography, Object.assign({ variant: "body2" }, { children: new Date(job.createdAt).toLocaleString() }))] })), job.errorMessage && (_jsxs(_Fragment, { children: [_jsx(Typography, Object.assign({ variant: "body1", sx: { fontWeight: "bold", color: "#EF5350" } }, { children: "Error:" })), _jsx(Typography, Object.assign({ variant: "body2", sx: { color: "#EF5350" } }, { children: job.errorMessage }))] })), job.jobStatus === "completed" && job.jobFiles && (_jsxs(_Fragment, { children: [_jsx(Typography, Object.assign({ variant: "body1", sx: { fontWeight: "bold" } }, { children: "Generated Content:" })), _jsx(Typography, Object.assign({ variant: "body2" }, { children: "Job completed successfully. View the generated image above." }))] })), _jsxs(Stack, Object.assign({ sx: {
                                        justifyContent: "center",
                                        marginTop: "20px",
                                        gap: 1,
                                        alignItems: "center",
                                    } }, { children: [job.jobStatus === "completed" && (_jsxs(_Fragment, { children: [_jsx(Stack, Object.assign({ direction: "row", sx: {
                                                        justifyContent: "center",
                                                        width: "100%",
                                                    } }, { children: _jsx(Button, Object.assign({ variant: "contained", color: "success", onClick: () => onCopyToPost(job), startIcon: _jsx(IosShare, {}), sx: { margin: "0.5em" } }, { children: "CREATE POST" })) })), _jsxs(Stack, Object.assign({ direction: "row", sx: {
                                                        justifyContent: "center",
                                                        gap: 1,
                                                        width: "100%",
                                                    } }, { children: [onCopyToNewJob && (_jsx(Button, Object.assign({ variant: "outlined", color: "primary", onClick: () => onCopyToNewJob(job), startIcon: _jsx(ContentCopyIcon, {}), sx: { margin: "0.5em" } }, { children: "COPY TO NEW JOB" }))), _jsx(Button, Object.assign({ variant: "outlined", color: "error", onClick: handleDeleteClick, startIcon: _jsx(DeleteIcon, {}), sx: { margin: "0.5em" } }, { children: "Delete Job" }))] }))] })), job.jobStatus === "failed" && (_jsxs(Stack, Object.assign({ direction: "row", sx: {
                                                justifyContent: "center",
                                                gap: 1,
                                                width: "100%",
                                            } }, { children: [onCopyToNewJob && (_jsx(Button, Object.assign({ variant: "outlined", color: "primary", onClick: () => onCopyToNewJob(job), startIcon: _jsx(ContentCopyIcon, {}), sx: { margin: "0.5em" } }, { children: "COPY TO NEW JOB" }))), _jsx(Button, Object.assign({ variant: "outlined", color: "error", onClick: handleDeleteClick, startIcon: _jsx(DeleteIcon, {}), sx: { margin: "0.5em" } }, { children: "Delete Job" }))] }))), job.jobStatus !== "completed" &&
                                            job.jobStatus !== "failed" && (_jsx(Stack, Object.assign({ direction: "row", sx: {
                                                justifyContent: "center",
                                                width: "100%",
                                            } }, { children: _jsx(Button, Object.assign({ variant: "outlined", color: "error", onClick: handleDeleteClick, startIcon: _jsx(DeleteIcon, {}), sx: { margin: "0.5em" } }, { children: "Delete Job" })) }))), _jsx(Stack, Object.assign({ direction: "row", sx: {
                                                justifyContent: "center",
                                                width: "100%",
                                            } }, { children: _jsx(Button, Object.assign({ variant: "contained", onClick: onBack, startIcon: _jsx(CloseIcon, {}), sx: { margin: "0.5em" } }, { children: "Close" })) }))] }))] })) }, "job-detail")] }))] })));
};
export const Studio = () => {
    var _a;
    const [loading, setLoading] = useState(false);
    const [selectedJobType, setSelectedJobType] = useState(null);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [createdJobTitle, setCreatedJobTitle] = useState("");
    const [showAllIncomplete, setShowAllIncomplete] = useState(false);
    const [showAllCompleted, setShowAllCompleted] = useState(false);
    const [selectedJob, setSelectedJob] = useState(null);
    const [selectedJobRecentMediaPath, setSelectedJobRecentMediaPath] = useState(null);
    const [jobIdProcessed, setJobIdProcessed] = useState(false);
    const [showChannelModal, setShowChannelModal] = useState(false);
    const [selectedStudioJob, setSelectedStudioJob] = useState(null);
    const [userChannels, setUserChannels] = useState([]);
    // State management for copy mode functionality
    const [copyJobData, setCopyJobData] = useState(null);
    // Ref for the form container to enable scrolling
    const formContainerRef = useRef(null);
    // Ref for the polling interval
    const pollingIntervalRef = useRef(null);
    // Track jobs that need countdown completion polling
    const [jobsAwaitingCountdown, setJobsAwaitingCountdown] = useState(new Set());
    // Add exponential backoff state for polling errors
    const [pollingErrorCount, setPollingErrorCount] = useState(0);
    // Track if a reload is in progress to prevent hammering the server
    const reloadInProgressRef = useRef(false);
    const lastReloadTimeRef = useRef(0);
    const auth = useProfileAuthStore((state) => state);
    const cacheBusterStore = useCacheBusterStore((state) => state);
    const navigate = useNavigate();
    const logger = useLogger((state) => state);
    const studioJobStore = useStudioJobStore();
    const [searchParams] = useSearchParams();
    useEffect(() => {
        const retVal = () => { };
        if (!auth.isSignedIn(logger) || !auth.userId) {
            if (!restoreSession(auth, logger)) {
                logger.log(LOGPREFIX + "User NOT logged in - saving redirect and navigating...");
                // Save the current URL before redirecting to welcome page for post-login redirect
                saveLoginRedirect(window.location.href);
                navigate("/Welcome", { replace: true });
                return retVal;
            }
        }
        else {
            // Load jobs when authenticated (always refresh on page load)
            if (!studioJobStore.loading) {
                studioJobStore.loadJobs(auth, cacheBusterStore, logger);
                // Also load server status to get model durations
                studioJobStore.getServerStatus(auth, cacheBusterStore, logger);
            }
            // Load channels user can post to
            loadUserChannels();
        }
        return retVal;
    }, [auth.isSignedIn(logger)]);
    const loadUserChannels = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const channels = yield listChannelsUserCanPost(auth, cacheBusterStore, logger);
            setUserChannels(channels);
        }
        catch (error) {
            logger.error(LOGPREFIX + "Error loading channels: " + error.message);
        }
    });
    // Handle jobId query parameter auto-selection
    useEffect(() => {
        const jobId = searchParams.get("jobId");
        logger.log(LOGPREFIX + "jobId = " + jobId);
        if (jobId &&
            !jobIdProcessed &&
            !studioJobStore.loading &&
            studioJobStore.jobs.length > 0) {
            logger.log(LOGPREFIX + "Processing jobId query parameter: " + jobId);
            // Search for the job in all jobs (both completed and incomplete)
            const foundJob = studioJobStore.jobs.find((job) => job.jobId.toLowerCase() === jobId.toLowerCase());
            if (foundJob) {
                logger.log(LOGPREFIX + "Found job for auto-selection: " + foundJob.jobId);
                handleJobClick(foundJob);
            }
            else {
                logger.log(LOGPREFIX + "Job not found for jobId: " + jobId);
            }
            // Mark as processed to prevent repeated attempts
            setJobIdProcessed(true);
        }
    }, [
        searchParams,
        jobIdProcessed,
        studioJobStore.loading,
        studioJobStore.jobs.length,
    ]);
    // Handle scrolling to form when selectedJobType changes
    useEffect(() => {
        if (selectedJobType && formContainerRef.current) {
            // Use setTimeout to ensure the DOM has updated before scrolling
            setTimeout(() => {
                var _a;
                (_a = formContainerRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            }, 100);
        }
    }, [selectedJobType]);
    // Load recent media path when job is selected
    useEffect(() => {
        if (selectedJob &&
            (selectedJob.jobType === "image-to-image" ||
                selectedJob.jobType === "image-to-video") &&
            selectedJob.sourceImagePath) {
            const loadRecentMediaPath = () => __awaiter(void 0, void 0, void 0, function* () {
                try {
                    const recentMediaFiles = yield studioJobStore.listRecentMedia(auth, cacheBusterStore, logger);
                    const matchingFile = recentMediaFiles.find((file) => file.filename.startsWith(selectedJob.jobId + "_input_"));
                    if (matchingFile && matchingFile.fullPath) {
                        setSelectedJobRecentMediaPath(matchingFile.fullPath);
                        logger.log("Recent media path found for job: " + matchingFile.fullPath);
                    }
                    else {
                        setSelectedJobRecentMediaPath(null);
                        logger.log("No recent media found for job: " + selectedJob.jobId);
                    }
                }
                catch (error) {
                    logger.error("Error loading recent media for job: " + error);
                    setSelectedJobRecentMediaPath(null);
                }
            });
            loadRecentMediaPath();
        }
        else {
            setSelectedJobRecentMediaPath(null);
        }
    }, [selectedJob, auth.isSignedIn(logger), studioJobStore.jobs]);
    // Clean up countdown tracking when jobs complete or fail
    useEffect(() => {
        const completedOrFailedJobs = studioJobStore.jobs.filter((job) => job.jobStatus === "completed" || job.jobStatus === "failed");
        if (completedOrFailedJobs.length > 0) {
            setJobsAwaitingCountdown((prev) => {
                const newSet = new Set(prev);
                completedOrFailedJobs.forEach((job) => {
                    if (newSet.has(job.jobId)) {
                        logger.log(LOGPREFIX + "Removing job from countdown tracking: " + job.jobId);
                        newSet.delete(job.jobId);
                    }
                });
                return newSet;
            });
        }
    }, [studioJobStore.jobs]);
    // Polling mechanism for jobs that need polling
    useEffect(() => {
        // Jobs that need polling:
        // 1. Pending jobs (always poll)
        // 2. Processing jobs that have completed their countdown
        const jobsNeedingPolling = studioJobStore.jobs.filter((job) => job.jobStatus === "pending" ||
            (job.jobStatus === "processing" &&
                jobsAwaitingCountdown.has(job.jobId)));
        const shouldPoll = auth.isSignedIn(logger) && jobsNeedingPolling.length > 0;
        // Always clear existing interval first
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
        if (shouldPoll) {
            // Calculate polling interval with exponential backoff
            const baseInterval = 15000; // 15 seconds
            const maxInterval = 120000; // 2 minutes max
            // Reset to base interval when countdown completes for processing jobs
            const hasCountdownCompletedJobs = jobsNeedingPolling.some((job) => job.jobStatus === "processing" &&
                jobsAwaitingCountdown.has(job.jobId));
            const backoffMultiplier = hasCountdownCompletedJobs
                ? 1
                : Math.min(Math.pow(2, pollingErrorCount), 8); // Cap at 8x
            const pollingInterval = Math.min(baseInterval * backoffMultiplier, maxInterval);
            logger.log(LOGPREFIX +
                `Starting polling for ${jobsNeedingPolling.length} jobs (${jobsNeedingPolling.filter((j) => j.jobStatus === "pending").length} pending, ${jobsNeedingPolling.filter((j) => j.jobStatus === "processing").length} processing with expired countdown). Interval: ${pollingInterval}ms (errors: ${pollingErrorCount})`);
            pollingIntervalRef.current = setInterval(() => __awaiter(void 0, void 0, void 0, function* () {
                try {
                    logger.log(LOGPREFIX + "Polling for job updates...");
                    yield studioJobStore.loadJobs(auth, cacheBusterStore, logger);
                    // Reset error count on successful poll
                    setPollingErrorCount(0);
                }
                catch (error) {
                    const errorMessage = error.message;
                    // Check if this is a sequence number error
                    if (errorMessage.includes("sequence number")) {
                        logger.log(LOGPREFIX +
                            "Sequence number error during polling - ignoring to prevent thrashing: " +
                            errorMessage);
                        // Don't increment error count for sequence errors, just log and continue
                        return; // Continue polling on normal interval
                    }
                    else {
                        // For other errors, increment error count for exponential backoff
                        logger.error(LOGPREFIX + "Error during polling: " + errorMessage);
                        setPollingErrorCount((prev) => prev + 1);
                        // Stop polling if too many consecutive errors
                        if (pollingErrorCount >= 5) {
                            logger.error(LOGPREFIX + "Too many polling errors, stopping polling");
                            if (pollingIntervalRef.current) {
                                clearInterval(pollingIntervalRef.current);
                                pollingIntervalRef.current = null;
                            }
                        }
                    }
                }
            }), pollingInterval);
        }
        else {
            logger.log(LOGPREFIX + "No jobs need polling - polling stopped");
            // Reset error count when stopping polling
            setPollingErrorCount(0);
        }
        // Cleanup on unmount
        return () => {
            if (pollingIntervalRef.current) {
                logger.log(LOGPREFIX + "Cleaning up polling interval");
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        };
    }, [studioJobStore.jobs, jobsAwaitingCountdown, auth.isSignedIn(logger)]);
    const handleCountdownComplete = (jobId) => {
        logger.log(LOGPREFIX + "Countdown completed for job: " + jobId);
        setJobsAwaitingCountdown((prev) => new Set([...prev, jobId]));
        // Reload job statuses 2 seconds after countdown completes
        setTimeout(() => __awaiter(void 0, void 0, void 0, function* () {
            // Throttle to prevent multiple rapid server calls
            const now = Date.now();
            const minInterval = 1000; // Minimum 1 second between reloads
            if (reloadInProgressRef.current) {
                logger.log(LOGPREFIX + "Reload already in progress, skipping");
                return;
            }
            if (now - lastReloadTimeRef.current < minInterval) {
                logger.log(LOGPREFIX + "Reload throttled, too soon since last reload");
                return;
            }
            try {
                reloadInProgressRef.current = true;
                lastReloadTimeRef.current = now;
                logger.log(LOGPREFIX + "Reloading jobs 2 seconds after countdown completion");
                // Get FRESH auth from store instead of using captured auth to prevent stale closure
                const currentAuth = useProfileAuthStore.getState();
                yield studioJobStore.loadJobs(currentAuth, cacheBusterStore, logger);
            }
            catch (error) {
                logger.error(LOGPREFIX +
                    "Error reloading jobs after countdown: " +
                    error.message);
            }
            finally {
                reloadInProgressRef.current = false;
            }
        }), 2000);
    };
    const handleJobTypeClick = (jobType) => {
        logger.log(LOGPREFIX + "Job type selected: " + jobType);
        setSelectedJobType(jobType);
    };
    const handleJobSubmit = (input) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setLoading(true);
            const newJob = yield studioJobStore.createJob(input, auth, cacheBusterStore, logger);
            logger.log(LOGPREFIX + "Job created successfully: " + newJob.jobId);
            // Set success message state
            setCreatedJobTitle(input.title || input.positivePrompt.substring(0, 50) + "...");
            setShowSuccessMessage(true);
            window.scrollTo(0, 0); // Scroll to top to show success message
            setSelectedJobType(null);
            setCopyJobData(null); // Clear copy data after successful submission
            // Refresh the jobs list
            yield studioJobStore.loadJobs(auth, cacheBusterStore, logger);
            // Schedule a quick follow-up check 2 seconds after creation to catch initial status update
            setTimeout(() => __awaiter(void 0, void 0, void 0, function* () {
                // Check if reload is throttled
                const now = Date.now();
                if (reloadInProgressRef.current ||
                    now - lastReloadTimeRef.current < 1000) {
                    logger.log(LOGPREFIX + "Quick status check throttled");
                    return;
                }
                try {
                    reloadInProgressRef.current = true;
                    lastReloadTimeRef.current = now;
                    logger.log(LOGPREFIX + "Quick status check 2 seconds after job creation");
                    const currentAuth = useProfileAuthStore.getState();
                    yield studioJobStore.loadJobs(currentAuth, cacheBusterStore, logger);
                }
                catch (error) {
                    logger.error(LOGPREFIX +
                        "Error in quick status check: " +
                        error.message);
                }
                finally {
                    reloadInProgressRef.current = false;
                }
            }), 2000);
        }
        catch (error) {
            logger.error(LOGPREFIX + "Error creating job: " + error.message);
            studioJobStore.setError(error.message);
        }
        finally {
            setLoading(false);
        }
    });
    const handleJobClick = (job) => {
        logger.log(LOGPREFIX + "Job clicked: " + job.jobId);
        setSelectedJob(job);
    };
    const handleCopyToPost = (job) => {
        logger.log(LOGPREFIX + "Copy to post clicked for job: " + job.jobId);
        setSelectedStudioJob(job);
        setShowChannelModal(true);
    };
    const handleCopyToNewJob = (job) => {
        logger.log(LOGPREFIX + "Copy to new job clicked for job: " + job.jobId);
        // Store the job data for copying
        setCopyJobData(job);
        // Set the appropriate job type and navigate to form
        setSelectedJobType(job.jobType);
        setSelectedJob(null); // Close job detail view
    };
    const handleChannelSelect = (channelId, channelName) => {
        if (!selectedStudioJob)
            return;
        // Find the selected channel to get the channelOwnerId
        const selectedChannel = userChannels.find((channel) => channel.channelId === channelId);
        if (!selectedChannel)
            return;
        logger.log(LOGPREFIX +
            `Channel selected: ${channelName} (${channelId}) for job: ${selectedStudioJob.jobId}`);
        // Navigate to channel with explicit IDs and studio job parameter
        const channelUrl = `/ChannelView?channelOwnerId=${selectedChannel.channelOwnerId}&channelId=${channelId}&postFromStudioJob=${selectedStudioJob.jobId}`;
        window.location.href = channelUrl;
    };
    const hasJobs = studioJobStore.jobs.length > 0;
    // Job type buttons
    const jobTypeButtons = JOB_TYPES.map((jobType) => (_jsx(JobTypeCard, { jobType: jobType, onClick: () => handleJobTypeClick(jobType.id) }, jobType.id)));
    // Use only API data - no localStorage fallback
    const allJobs = studioJobStore.jobs;
    // Incomplete jobs (pending, processing, failed)
    const incompleteJobs = allJobs.filter((job) => job.jobStatus === "pending" ||
        job.jobStatus === "processing" ||
        job.jobStatus === "failed");
    // Completed jobs (only successfully completed)
    const completedJobs = allJobs.filter((job) => job.jobStatus === "completed");
    // Create cards for each section with pagination
    const displayedIncompleteJobs = showAllIncomplete
        ? incompleteJobs
        : incompleteJobs.slice(0, 4);
    const displayedCompletedJobs = showAllCompleted
        ? completedJobs
        : completedJobs.slice(0, 4);
    const incompleteJobCards = displayedIncompleteJobs.map((job) => {
        var _a, _b, _c, _d, _e;
        const modelDuration = (_b = (_a = studioJobStore.serverStatus) === null || _a === void 0 ? void 0 : _a.executionDurations) === null || _b === void 0 ? void 0 : _b[job.modelId];
        // Debug logging for timer issues
        if (job.jobStatus === "processing") {
            console.log("Processing job timer check:", {
                jobId: job.jobId,
                modelId: job.modelId,
                serverStatus: studioJobStore.serverStatus,
                executionDurations: (_c = studioJobStore.serverStatus) === null || _c === void 0 ? void 0 : _c.executionDurations,
                modelDuration: modelDuration,
                hasServerStatus: !!studioJobStore.serverStatus,
                hasExecutionDurations: !!((_d = studioJobStore.serverStatus) === null || _d === void 0 ? void 0 : _d.executionDurations),
                executionDurationKeys: ((_e = studioJobStore.serverStatus) === null || _e === void 0 ? void 0 : _e.executionDurations)
                    ? Object.keys(studioJobStore.serverStatus.executionDurations)
                    : [],
            });
        }
        return (_jsx(StudioJobCard, { job: job, onClick: () => handleJobClick(job), modelDuration: modelDuration, onCountdownComplete: () => handleCountdownComplete(job.jobId) }, job.jobId));
    });
    const completedJobCards = displayedCompletedJobs.map((job) => (_jsx(StudioJobCard, { job: job, onClick: () => handleJobClick(job), onCopyToPost: handleCopyToPost }, job.jobId)));
    let mainContent;
    if (selectedJob) {
        // Show job detail view
        mainContent = (_jsx(JobDetail, { job: selectedJob, onBack: () => {
                setSelectedJob(null);
            }, onCopyToPost: handleCopyToPost, onCopyToNewJob: handleCopyToNewJob }));
    }
    else if (selectedJobType) {
        logger.log(LOGPREFIX + ": selectedJobType = " + selectedJobType);
        // Show form for selected job type
        mainContent = (_jsxs(Stack, Object.assign({ ref: formContainerRef, sx: { padding: "20px", alignItems: "center", marginTop: "5px" } }, { children: [_jsx(Stack, Object.assign({ direction: "row", sx: {
                        width: "100%",
                        justifyContent: "flex-end",
                        maxWidth: "100%",
                        padding: "0",
                    } }, { children: _jsx(Button, Object.assign({ variant: "contained", onClick: () => {
                            setSelectedJobType(null);
                            setCopyJobData(null); // Clear copy data when closing form
                        }, startIcon: _jsx(CloseIcon, {}), sx: { margin: "0.5em" }, disabled: loading }, { children: "Close" })) })), _jsx(NewStudioJobForm, { jobType: selectedJobType, onSubmit: handleJobSubmit, onCancel: () => {
                        setSelectedJobType(null);
                        setCopyJobData(null); // Clear copy data on cancel
                    }, loading: loading, auth: auth, logger: logger, initialJobData: copyJobData || undefined, recentMediaPath: selectedJobRecentMediaPath })] })));
    }
    else if (hasJobs) {
        logger.log(LOGPREFIX + ": hasJobs = true");
        // Show incomplete/completed jobs + job type buttons
        mainContent = (_jsxs(Stack, Object.assign({ sx: { padding: 0, margin: 0, marginTop: "5px" }, spacing: 2 }, { children: [incompleteJobs.length > 0 && (_jsxs(Stack, Object.assign({ sx: {
                        marginBottom: "10px",
                        marginTop: "5px",
                        marginLeft: "5px",
                        marginRight: "5px",
                    } }, { children: [_jsx(Divider, {}), _jsx(Typography, Object.assign({ variant: "h6", sx: { paddingLeft: "5px", paddingRight: "5px" } }, { children: "Incomplete Jobs" })), _jsx(ContentCardStacks, { keyStr: "incomplete-studio-jobs", cards: incompleteJobCards, columnCount: 4 }), incompleteJobs.length > 4 && !showAllIncomplete && (_jsx(Stack, Object.assign({ sx: { textAlign: "center", marginTop: "10px" } }, { children: _jsxs(Button, Object.assign({ variant: "outlined", onClick: () => setShowAllIncomplete(true), sx: { margin: "0.5em" } }, { children: ["Show More (", incompleteJobs.length - 4, " remaining)"] })) })))] }))), completedJobs.length > 0 && (_jsxs(Stack, Object.assign({ sx: {
                        marginBottom: "10px",
                        marginTop: "5px",
                        marginLeft: "5px",
                        marginRight: "5px",
                    } }, { children: [_jsx(Divider, {}), _jsx(Typography, Object.assign({ variant: "h6", sx: { paddingLeft: "5px", paddingRight: "5px" } }, { children: "Completed Jobs" })), _jsx(ContentCardStacks, { keyStr: "completed-studio-jobs", cards: completedJobCards, columnCount: 4 }), completedJobs.length > 4 && !showAllCompleted && (_jsx(Stack, Object.assign({ sx: { textAlign: "center", marginTop: "10px" } }, { children: _jsxs(Button, Object.assign({ variant: "outlined", onClick: () => setShowAllCompleted(true), sx: { margin: "0.5em" } }, { children: ["Show More (", completedJobs.length - 4, " remaining)"] })) }))), showAllCompleted && studioJobStore.hasMoreJobs && (_jsx(Stack, Object.assign({ sx: { textAlign: "center", marginTop: "10px" } }, { children: _jsx(Button, Object.assign({ variant: "outlined", onClick: () => studioJobStore.loadMoreJobs(auth, cacheBusterStore, logger), disabled: studioJobStore.loadingMore, sx: { margin: "0.5em" } }, { children: studioJobStore.loadingMore ? "Loading..." : "Load More Jobs" })) })))] }))), _jsxs(Stack, Object.assign({ sx: {
                        marginBottom: "10px",
                        marginTop: "5px",
                        marginLeft: "5px",
                        marginRight: "5px",
                    } }, { children: [_jsx(Divider, {}), _jsx(Typography, Object.assign({ variant: "h6", sx: { paddingLeft: "5px", paddingRight: "5px" } }, { children: "Create New Job" })), _jsx(ContentCardStacks, { keyStr: "job-type-buttons", cards: jobTypeButtons, columnCount: 4 })] }))] })));
    }
    else {
        // Show only job type buttons (no jobs state)
        mainContent = (_jsxs(Stack, Object.assign({ sx: { padding: "20px", alignItems: "center" }, spacing: 3 }, { children: [_jsx(Typography, Object.assign({ variant: "h5", color: "primary", sx: { textAlign: "center", marginBottom: "20px" } }, { children: "Welcome to AI Design Studio" })), _jsx(Typography, Object.assign({ variant: "body1", sx: { textAlign: "center", marginBottom: "20px", maxWidth: "600px" } }, { children: "Create amazing AI-generated content with our powerful tools. Choose a job type below to get started." })), _jsx(ContentCardStacks, { keyStr: "job-type-buttons-main", cards: jobTypeButtons, columnCount: 4 })] })));
    }
    // Success message component
    const successMessage = showSuccessMessage && (_jsx(Stack, Object.assign({ sx: { padding: "10px", marginBottom: "10px" } }, { children: _jsxs(Card, Object.assign({ sx: { backgroundColor: "#E8F5E8", border: "1px solid #4CAF50" } }, { children: [_jsx(CardContent, { children: _jsxs(Stack, Object.assign({ direction: "row", spacing: 2, alignItems: "flex-start" }, { children: [_jsx(CheckCircleIcon, { sx: { color: "#4CAF50", marginTop: "2px" } }), _jsxs(Stack, Object.assign({ spacing: 2, sx: { flex: 1 } }, { children: [_jsx(Typography, Object.assign({ variant: "h6", sx: { color: "#2E7D32", fontWeight: "bold" } }, { children: "Job Queued Successfully!" })), _jsxs(Typography, Object.assign({ variant: "body2", sx: { color: "#2E7D32" } }, { children: [_jsxs("strong", { children: ["\u201C", createdJobTitle, "\u201D"] }), " has been queued for processing by our servers. Jobs are handled in the order they were received."] })), _jsx(Typography, Object.assign({ variant: "body2", sx: { color: "#2E7D32" } }, { children: "We will send you a notification when your job completes or fails." }))] }))] })) }), _jsx(CardActions, { children: _jsx(Stack, Object.assign({ direction: "row", sx: { display: "flex", width: "100%", justifyContent: "center" } }, { children: _jsx(Button, Object.assign({ onClick: () => setShowSuccessMessage(false), sx: { margin: "0.5em" }, variant: "contained", color: "primary" }, { children: "OK" })) })) })] })) })));
    const content = (_jsxs(Stack, Object.assign({ sx: { padding: 0, margin: 0 }, spacing: 0 }, { children: [_jsx(TopButtonMenu, { current: "studio" }), ((_a = studioJobStore === null || studioJobStore === void 0 ? void 0 : studioJobStore.jobs) === null || _a === void 0 ? void 0 : _a.length) > 0 && (_jsx(Typography, Object.assign({ variant: "h5", color: "primary", sx: { textAlign: "center", margin: "10px" } }, { children: "AI Design Studio" }))), (studioJobStore.loading || loading) && (_jsx(LinearProgress, { sx: { width: "100%", margin: "2px" } }, "content.loading.spinner")), successMessage, studioJobStore.error && (_jsx(Stack, Object.assign({ sx: { padding: "10px" } }, { children: _jsxs(Typography, Object.assign({ variant: "body2", color: "error" }, { children: ["Error: ", studioJobStore.error] })) }))), mainContent, selectedStudioJob && (_jsx(ChannelSelectionModal, { open: showChannelModal, onClose: () => setShowChannelModal(false), studioJob: selectedStudioJob, channels: userChannels.map((channel) => ({
                    channelId: channel.channelId,
                    channelName: channel.title,
                    channelDescription: channel.description,
                    channelOwnerId: channel.channelOwnerId,
                })), onChannelSelect: handleChannelSelect }))] }), "studio-content"));
    const output = (_jsx("div", Object.assign({ style: { width: "100%", padding: 0, margin: 0 }, id: "outer", "data-testid": "studio-page" }, { children: _jsxs(ThemeProvider, Object.assign({ theme: getTheme() }, { children: [_jsx(Heading, {}), _jsx(Container, Object.assign({ sx: {
                        backgroundColor: "#FFFFFF",
                        width: "100%",
                        margin: 0,
                        padding: 0,
                    }, maxWidth: false }, { children: content }), "studio.container"), _jsx(Footer, {})] })) }), "page.div.studio"));
    return output;
};
